/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import React, { type JSX } from 'react';

import {
  // INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {INSERT_HORIZONTAL_RULE_COMMAND} from '@lexical/extension';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {INSERT_TABLE_COMMAND} from '@lexical/table';
import { OPEN_TABLE_SELECTOR_COMMAND } from '@/components/editor/lexicalPlugins/tables/TablesPlugin';
import {
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
  TextNode,
} from 'lexical';
import { $isCodeNode } from '@lexical/code';
import {useCallback, useMemo, useState} from 'react';
import * as ReactDOM from 'react-dom';

import { useDialog, type OpenDialogContextType } from '@/components/common/withDialog';
import { applyBlockTypeChange } from '../ToolbarPlugin/utils';
import { INSERT_COLLAPSIBLE_SECTION_COMMAND } from '@/components/editor/lexicalPlugins/collapsibleSections/CollapsibleSectionsPlugin';
import { INSERT_SPOILER_COMMAND } from '@/components/editor/lexicalPlugins/spoilers/SpoilersPlugin';
import { OPEN_MATH_EDITOR_COMMAND } from '@/components/editor/lexicalPlugins/math/MathPlugin';
import { INSERT_FOOTNOTE_COMMAND } from '@/components/editor/lexicalPlugins/footnotes/FootnotesPlugin';
import {InsertImageDialog} from '../ImagesPlugin';
import {INSERT_EXCALIDRAW_COMMAND} from '../ExcalidrawPlugin/commands';

import { TableIcon } from '../../icons/TableIcon';
import { TextParagraphIcon } from '../../icons/TextParagraphIcon';
import { TypeH1Icon } from '../../icons/TypeH1Icon';
import { TypeH2Icon } from '../../icons/TypeH2Icon';
import { TypeH3Icon } from '../../icons/TypeH3Icon';
import { ListOlIcon } from '../../icons/ListOlIcon';
import { ListUlIcon } from '../../icons/ListUlIcon';
import { ChatSquareQuoteIcon } from '../../icons/ChatSquareQuoteIcon';
import { CodeIcon } from '../../icons/CodeIcon';
import { HorizontalRuleIcon } from '../../icons/HorizontalRuleIcon';
import { CardChecklistIcon } from '../../icons/CardChecklistIcon';
import { PlusSlashMinusIcon } from '../../icons/PlusSlashMinusIcon';
import { FileImageIcon } from '../../icons/FileImageIcon';
import { Diagram2Icon } from '../../icons/Diagram2Icon';
import { CaretRightFillIcon } from '../../icons/CaretRightFillIcon';
import { CkFootnoteIcon } from '../../icons/CkFootnoteIcon';
import ForumIcon from '@/components/common/ForumIcon';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import classNames from 'classnames';
import { useCurrentUser } from '@/components/common/withUser';
import { userIsAdmin } from '@/lib/vulcan-users/permissions';
import { useResearchEditorEnvironmentOptional } from '@/components/research/lexical/ResearchEditorContext';
import { INSERT_QUERY_INPUT_COMMAND } from '@/components/research/lexical/QueryInputPlugin';
import { InsertReviewResultsDialog } from '../../embeds/ReviewResultsEmbed/InsertReviewResultsDialog';
import { INSERT_IFRAME_WIDGET_COMMAND } from '../../embeds/IframeWidgetEmbed/IframeWidgetPlugin';
import { INSERT_LLM_CONTENT_BLOCK_COMMAND } from '@/components/editor/lexicalPlugins/llmContentOutput/LLMContentBlockPlugin';
import {
  typeaheadPopover,
  typeaheadList,
  typeaheadListItem,
  typeaheadItem,
  typeaheadItemText,
  typeaheadItemIcon,
  componentPickerMenu,
} from '../../styles/typeaheadStyles';
import omit from 'lodash/omit';

const styles = defineStyles('LexicalComponentPicker', (theme: ThemeType) => ({
  popover: {
    ...typeaheadPopover(theme),
    ...componentPickerMenu(),
  },
  list: {
    ...typeaheadList(theme),
    maxHeight: 400,
  },
  listItem: typeaheadListItem(theme),
  item: typeaheadItem(theme),
  text: typeaheadItemText(),
  icon: {
    ...typeaheadItemIcon(),
    opacity: 0.6,
  },
}));

const iconStyle = { display: 'flex', width: 18, height: 18, marginRight: 8, marginTop: 2, opacity: 0.6 };

class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: JSX.Element;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option?
  onSelect: (queryString: string) => void;

  constructor(
    title: string,
    options: {
      icon?: JSX.Element;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: (queryString: string) => void;
    },
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
  }
}

function ComponentPickerMenuItem({index, isSelected, onClick, onMouseEnter, option}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: ComponentPickerOption;
}) {
  const classes = useStyles(styles);

  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={classNames(classes.item, classes.listItem, { selected: isSelected })}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}>
      {option.icon}
      <span className={classes.text}>{option.title}</span>
    </li>
  );
}

function getDynamicOptions(editor: LexicalEditor, queryString: string) {
  const options: Array<ComponentPickerOption> = [];

  if (queryString == null) {
    return options;
  }

  const tableMatch = queryString.match(/^([1-9]\d?)(?:x([1-9]\d?)?)?$/);

  if (tableMatch !== null) {
    const rows = tableMatch[1];
    const colOptions = tableMatch[2]
      ? [tableMatch[2]]
      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(String);

    options.push(
      ...colOptions.map(
        (columns) =>
          new ComponentPickerOption(`${rows}x${columns} Table`, {
            icon: <TableIcon style={iconStyle} />,
            keywords: ['table'],
            onSelect: () =>
              editor.dispatchCommand(INSERT_TABLE_COMMAND, {columns, rows}),
          }),
      ),
    );
  }

  return options;
}

const headingIcons = {
  1: TypeH1Icon,
  2: TypeH2Icon,
  3: TypeH3Icon,
} as const;


function useBaseOptions(
  editor: LexicalEditor,
  openDialog: OpenDialogContextType['openDialog'],
  currentUser: UsersCurrent | null,
  inResearchContext: boolean,
) {
  const classes = useStyles(styles);
  const isAdminUser = userIsAdmin(currentUser);
  return [
    new ComponentPickerOption('Table', {
      icon: <TableIcon style={iconStyle} />,
      keywords: ['table', 'grid', 'spreadsheet', 'rows', 'columns'],
      onSelect: () => {
        const nativeSelection = window.getSelection();
        let anchorRect: DOMRect | null = null;
        if (nativeSelection && nativeSelection.rangeCount > 0) {
          anchorRect = nativeSelection.getRangeAt(0).getBoundingClientRect();
        }
        editor.dispatchCommand(OPEN_TABLE_SELECTOR_COMMAND, anchorRect);
      },
    }),
    new ComponentPickerOption('Numbered List', {
      icon: <ListOlIcon style={iconStyle} />,
      keywords: ['numbered list', 'ordered list', 'ol'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption('Bulleted List', {
      icon: <ListUlIcon style={iconStyle} />,
      keywords: ['bulleted list', 'unordered list', 'ul'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption('Blockquote', {
      icon: <ChatSquareQuoteIcon style={iconStyle} />,
      keywords: ['blockquote', 'block', 'quote'],
      onSelect: () =>
        applyBlockTypeChange(editor, 'quote'),
    }),
    new ComponentPickerOption('Divider', {
      icon: <HorizontalRuleIcon style={iconStyle} />,
      keywords: ['horizontal rule', 'divider', 'hr'],
      onSelect: () => {
        editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
      }
    }),
    new ComponentPickerOption('Footnote', {
      icon: <CkFootnoteIcon className={classes.icon} />,
      keywords: ['footnote', 'note', 'reference'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_FOOTNOTE_COMMAND, {}),
    }),
    new ComponentPickerOption('Inline Equation', {
      icon: <PlusSlashMinusIcon style={iconStyle} />,
      keywords: ['equation', 'latex', 'math', 'inline'],
      onSelect: () =>
        editor.dispatchCommand(OPEN_MATH_EDITOR_COMMAND, { inline: true }),
    }),
    new ComponentPickerOption('Display Equation', {
      icon: <PlusSlashMinusIcon style={iconStyle} />,
      keywords: ['display', 'equation', 'latex', 'math', 'block'],
      onSelect: () =>
        editor.dispatchCommand(OPEN_MATH_EDITOR_COMMAND, { inline: false }),
    }),
    new ComponentPickerOption('Image', {
      icon: <FileImageIcon style={iconStyle} />,
      keywords: ['image', 'photo', 'picture', 'file'],
      onSelect: () =>
        openDialog({
          name: 'InsertImageDialog',
          contents: ({ onClose }) => (
            <InsertImageDialog activeEditor={editor} onClose={onClose} />
          ),
        }),
    }),
    new ComponentPickerOption('Diagram', {
      icon: <Diagram2Icon style={iconStyle} />,
      keywords: ['diagram', 'drawing', 'excalidraw', 'sketch', 'flowchart', 'whiteboard', 'arrows'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined),
    }),
    new ComponentPickerOption('Collapsible Section', {
      icon: <CaretRightFillIcon style={iconStyle} />,
      keywords: ['collapse', 'collapsible', 'toggle', 'section'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_COLLAPSIBLE_SECTION_COMMAND, undefined),
    }),
    new ComponentPickerOption('Spoiler Block', {
      icon: <ForumIcon icon="EyeSlash" style={omit(iconStyle, 'marginTop')} />,
      keywords: ['spoiler', 'hidden', 'hide', 'reveal', 'blur'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_SPOILER_COMMAND, undefined),
    }),
    new ComponentPickerOption('Custom Widget', {
      icon: <CodeIcon style={iconStyle} />,
      keywords: ['custom', 'iframe', 'widget', 'html', 'embed', 'javascript', 'interactive'],
      onSelect: () => {
        editor.dispatchCommand(INSERT_IFRAME_WIDGET_COMMAND, undefined);
      },
    }),
    new ComponentPickerOption('LLM Content', {
      icon: <ForumIcon icon="Robot" style={omit(iconStyle, 'marginTop')} />,
      keywords: ['ai', 'llm', 'model', 'generated', 'language model', 'chatbot'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_LLM_CONTENT_BLOCK_COMMAND, undefined),
    }),
    new ComponentPickerOption('Code Block', {
      icon: <CodeIcon style={iconStyle} />,
      keywords: ['javascript', 'python', 'js', 'code block', 'code', 'block'],
      onSelect: () =>
        applyBlockTypeChange(editor, 'code'),
    }),
    ...(isAdminUser && !inResearchContext ? [
      new ComponentPickerOption('Review Results Table', {
        icon: <CardChecklistIcon style={iconStyle} />,
        keywords: ['review', 'results', 'annual', 'voting', 'table'],
        onSelect: () =>
          openDialog({
            name: 'InsertReviewResultsDialog',
            contents: ({ onClose }) => (
              <InsertReviewResultsDialog activeEditor={editor} onClose={onClose} />
            ),
          }),
      }),
    ] : []),
    new ComponentPickerOption('Paragraph', {
      icon: <TextParagraphIcon style={iconStyle} />,
      keywords: ['normal', 'paragraph', 'p', 'text'],
      onSelect: () =>
        applyBlockTypeChange(editor, 'paragraph'),
    }),
    ...([1, 2, 3] as const).map(
      (n) => {
        const HeadingIcon = headingIcons[n];
        return new ComponentPickerOption(`Heading ${n}`, {
          icon: <HeadingIcon style={iconStyle} />,
          keywords: ['heading', 'header', `h${n}`],
          onSelect: () =>
            applyBlockTypeChange(editor, `h${n}`),
        });
      }
    ),
  ];
}

interface ResearchSlashCustomizations {
  extras: ComponentPickerOption[];
}

function useResearchSlashCustomizations(editor: LexicalEditor): ResearchSlashCustomizations | null {
  const env = useResearchEditorEnvironmentOptional();
  return useMemo(() => {
    if (!env) return null;
    const queryOption = new ComponentPickerOption('Query (research agent)', {
      icon: <ForumIcon icon="Robot" style={omit(iconStyle, 'marginTop')} />,
      keywords: ['query', 'agent', 'ask', 'research', 'claude'],
      onSelect: () => {
        editor.dispatchCommand(INSERT_QUERY_INPUT_COMMAND, undefined);
      },
    });
    return { extras: [queryOption] };
  }, [env, editor]);
}

export default function ComponentPickerMenuPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const { openDialog } = useDialog();
  const currentUser = useCurrentUser();
  const researchCustomizations = useResearchSlashCustomizations(editor);

  const baseCheckForTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    allowWhitespace: true,
    minLength: 0,
  });

  // Suppress the slash menu inside code blocks. Previously this was
  // implicit because CodeHighlightNode.isSimpleText() returned false,
  // but plain TextNode children pass that check.
  const checkForTriggerMatch = useCallback(
    (text: string, editorInstance: LexicalEditor) => {
      const inCode = editorInstance.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        const anchor = selection.anchor.getNode();
        const parent = anchor.getParent();
        return $isCodeNode(anchor) || $isCodeNode(parent);
      });
      if (inCode) return null;
      return baseCheckForTriggerMatch(text, editorInstance);
    },
    [baseCheckForTriggerMatch],
  );

  const baseOptions = useBaseOptions(editor, openDialog, currentUser, !!researchCustomizations);

  const options = useMemo(() => {
    const allOptions: ComponentPickerOption[] = [
      ...(researchCustomizations?.extras ?? []),
      ...baseOptions,
    ];

    if (!queryString) {
      return allOptions;
    }

    const regex = new RegExp(queryString, 'i');

    return [
      ...getDynamicOptions(editor, queryString),
      ...allOptions.filter(
        (option) =>
          regex.test(option.title) ||
          option.keywords.some((keyword) => regex.test(keyword)),
      ),
    ];
  }, [editor, queryString, baseOptions, researchCustomizations]);

  const onSelectOption = useCallback(
    (
      selectedOption: ComponentPickerOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
      matchingString: string,
    ) => {
      editor.update(() => {
        nodeToRemove?.remove();
        selectedOption.onSelect(matchingString);
        closeMenu();
      });
    },
    [editor],
  );

  const classes = useStyles(styles);

  return (
    <>
      <LexicalTypeaheadMenuPlugin<ComponentPickerOption>
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={options}
        menuRenderFn={(
          anchorElementRef,
          {selectedIndex, selectOptionAndCleanUp, setHighlightedIndex},
        ) =>
          anchorElementRef.current && options.length
            ? ReactDOM.createPortal(
                <div className={classes.popover}>
                  <ul className={classes.list}>
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                      />
                    ))}
                  </ul>
                </div>,
                anchorElementRef.current,
              )
            : null
        }
      />
    </>
  );
}
